import styled from 'styled-components';

import ConditionalLink, { ConditionalLinkTypes } from 'components/conditionalLink';
import { Flex } from 'components/flex';

const Card = styled.div`
  width: 280px;
  height: 334px;
  position: relative;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  border: 1px solid;
  border-radius: 4px;

  z-index: 3;

  &::after {
    content: '';
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 0.8) 100%);
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 3px;
  }
`;

const Head = styled.div`
  z-index: 5;
`;

const Tag = styled.i`
  font-size: 0.625rem;
  font-weight: 900;
  text-transform: uppercase;
  font-style: normal;
  padding: 2px 6px;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.white};

  &.new {
    background: ${({ theme }) => theme.colors.alert_400};
  }

  &.soon {
    background: ${({ theme }) => theme.colors.secondary_400};
  }

  &.unavailable {
    background: ${({ theme }) => theme.colors.dark_400};
  }
`;

const Tags = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 1rem;
  gap: 0.625rem;
`;

const Foot = styled.div`
  padding: 1rem;
  z-index: 5;
`;

const Hat = styled.div`
  font-size: 0.75rem;
  line-height: 0.875rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  text-transform: uppercase;
`;

const Name = styled.div`
  font-size: 1.125rem;
  line-height: 1.313rem;
  font-weight: 700;
`;

const Background = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0.5;
  border-radius: 3px;
`;

const Wrapper = styled(ConditionalLink)`
  width: 280px;
  height: 340px;
  position: relative;
  display: block;

  ${Card} {
    background-color: ${(props: any) => (props['data-theme'] === 'light' ? props.theme.colors.white : props.theme.colors.white)};
    border-color: ${(props: any) => (props['data-theme'] === 'light' ? props.theme.colors.gray_300 : props.theme.colors.dark_150)};

    ${(props: any) => {
      if (props['data-image']) {
        return `
          background: ${props.theme.colors.gray_500} url(${props['data-image']});
          background-size: cover;
          background-position: top center;
        `;
      }
    }}
  }

  ${Hat} {
    color: ${({ theme }) => theme.colors.primary_400};
  }

  ${Name} {
    color: ${(props: any) => (props['data-image'] ? props.theme.colors.white : props.theme.colors.dark_350)};
  }

  &::before {
    content: '';
    display: block;
    width: 276px;
    height: 30px;
    background-color: ${(props: any) => (props['data-image'] ? props.theme.colors.dark_350 : props.theme.colors.white)};
    border: 1px solid ${(props: any) => (props['data-theme'] === 'light' ? props.theme.colors.gray_300 : props.theme.colors.dark_150)};
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: 3px;
    border-radius: 4px;
    z-index: 2;
  }

  &::after {
    content: '';
    display: block;
    width: 270px;
    height: 30px;
    background-color: ${(props: any) => (props['data-image'] ? props.theme.colors.dark_350 : props.theme.colors.white)};
    border: 1px solid ${(props: any) => (props['data-theme'] === 'light' ? props.theme.colors.gray_300 : props.theme.colors.dark_150)};
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    bottom: 0;
    border-radius: 4px;
    z-index: 1;
  }

  ${(props: any) => {
    if (!props['data-disabled']) {
      return `
        &:hover {
          ${Background} {
            background: linear-gradient(0deg, ${props.theme.colors.primary_400}, ${props.theme.colors.primary_400}),
              linear-gradient(90deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.1) 100%);
            background-blend-mode: multiply, normal, normal;
          }

          ${Card} {
            border-color: ${props.theme.colors.primary_400};
          }

          &::before,
          &::after {
            border-color: ${props.theme.colors.primary_400};
          }
        }
      `;
    }
  }}
`;

interface ProgramProps {
  hat?: string;
  name: string;
  image: string;
  enrolled: boolean;

  link?: string;
  disabled?: boolean;
  theme?: 'light' | 'dark';
  tags?: ('new' | 'soon' | 'unavailable')[];
}

const Program = ({ hat, name, image, link, disabled = false, tags = [], enrolled = false, theme = 'light' }: ProgramProps) => {
  const linkType: ConditionalLinkTypes = !disabled ? 'internal' : 'nolink';

  return (
    <Wrapper to={link || ''} type={linkType} component={Flex} data-theme={theme} data-enrolled={enrolled} data-disabled={disabled} data-image={image}>
      <Card>
        <Head>
          <Tags>
            {tags.includes('new') && <Tag className="new">Novo</Tag>}
            {tags.includes('soon') && <Tag className="soon">Em breve</Tag>}
            {tags.includes('unavailable') && <Tag className="unavailable">Indisponível</Tag>}
          </Tags>
        </Head>

        <Foot>
          {!!hat && <Hat>{hat}</Hat>}
          <Name>{name}</Name>
        </Foot>

        <Background />
      </Card>
    </Wrapper>
  );
};

export default Program;
